import type { TokenResponse } from "./types";

/**
 * Token storage (localStorage) + the signed-in user decoded from the JWT.
 * Components read it with useAuth() (useSyncExternalStore); other tabs are kept in sync via the storage event.
 */

const KEYS = {
  access: "wb.member.accessToken",
  expires: "wb.member.accessTokenExpiresAt",
  refresh: "wb.member.refreshToken",
} as const;

export interface AuthUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  roles: string[];
}

export interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  /** Epoch ms. */
  expiresAt: number;
  user: AuthUser | null;
}

function read(key: string) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string | null) {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // Storage unavailable (private mode): the session lives in memory only.
  }
}

function base64UrlDecode(part: string) {
  const b64 = part.replace(/-/g, "+").replace(/_/g, "/");
  const padded = b64.padEnd(b64.length + ((4 - (b64.length % 4)) % 4), "=");
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

const ROLE_CLAIM = "http://schemas.microsoft.com/ws/2008/06/identity/claims/role";

/** Reads sub/email/given_name/family_name/role from the access token payload. */
export function decodeUser(jwt: string | null): AuthUser | null {
  if (!jwt) return null;
  try {
    const payload = JSON.parse(base64UrlDecode(jwt.split(".")[1] ?? "")) as Record<string, unknown>;
    const rawRoles = payload["role"] ?? payload[ROLE_CLAIM] ?? [];
    const roles = (Array.isArray(rawRoles) ? rawRoles : [rawRoles]).map(String);
    const firstName = String(payload["given_name"] ?? "");
    const lastName = String(payload["family_name"] ?? "");
    const email = String(payload["email"] ?? payload["name"] ?? "");
    return {
      id: String(payload["sub"] ?? ""),
      email,
      firstName,
      lastName,
      fullName: `${firstName} ${lastName}`.trim() || email,
      roles,
    };
  } catch {
    return null;
  }
}

function load(): AuthState {
  const accessToken = read(KEYS.access);
  const refreshToken = read(KEYS.refresh);
  const expiresAt = Date.parse(read(KEYS.expires) ?? "") || 0;
  return { accessToken, refreshToken, expiresAt, user: decodeUser(accessToken) };
}

let state: AuthState = load();
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((l) => l());
}

export const authStore = {
  get: () => state,
  subscribe(listener: () => void) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  /** Signed in = a refresh token exists (an expired access token is refreshed on the next call). */
  isAuthenticated: () => state.refreshToken !== null,
  setTokens(tokens: TokenResponse) {
    write(KEYS.access, tokens.accessToken);
    write(KEYS.expires, tokens.accessTokenExpiresAt);
    write(KEYS.refresh, tokens.refreshToken);
    state = {
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      expiresAt: Date.parse(tokens.accessTokenExpiresAt) || 0,
      user: decodeUser(tokens.accessToken),
    };
    emit();
  },
  clear() {
    const wasSignedIn = state.refreshToken !== null;
    write(KEYS.access, null);
    write(KEYS.expires, null);
    write(KEYS.refresh, null);
    state = { accessToken: null, refreshToken: null, expiresAt: 0, user: null };
    if (wasSignedIn) emit();
  },
};

if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key && Object.values(KEYS).includes(e.key as (typeof KEYS)[keyof typeof KEYS])) {
      state = load();
      emit();
    }
  });
}
